/**
 * Where `SlideOut` and `Drawer` overlays mount.
 *
 * ## Why not `document.body`
 *
 * `SlideOut`'s default portal is `body`, which makes its backdrop `fixed
 * inset-0` — the whole viewport, including the shell's top bar and module
 * side nav. Both studios reject that: an overlay opened from a list inside
 * this app is about this app, and dimming the shell's own navigation says the
 * whole product is blocked when only the content column is.
 *
 * Governance and Agentic Studio both solve it the same way — an absolutely
 * positioned, `pointer-events-none` root that spans the content column, with
 * the panel portaled into it — and this is that pattern, third copy. See
 * `aims-os-governance/src/shared/utils/drawerPortalTarget.js` and
 * `aims-os-agentic-studio/src/shared/utils/drawerPortalTarget.js`.
 *
 * `SlideOut` needs no flag for this: it already switches its layers from
 * `fixed` to `absolute` when the portal target is not `body`.
 *
 * ## Why the id lives here
 *
 * The studios keep theirs in an app-wide constants module because they
 * already had one. This app does not, and one constant does not earn a file.
 */

export const DASHBOARDS_OVERLAY_ROOT_ID = 'dashboards-overlay-root';

/**
 * The overlay root, or `document.body` when it is not mounted — a panel that
 * covers too much is still better than a panel that does not open.
 *
 * @returns {HTMLElement|null}
 */
export const getDashboardsDrawerPortalTarget = () => {
  if (typeof document === 'undefined') return null;
  return document.getElementById(DASHBOARDS_OVERLAY_ROOT_ID) ?? document.body;
};
